import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, ArrowRight, X, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FosButton } from '@/components/ui/flipperos';
import { useTour } from './useTour';
import type { TourPlacement, TourStep } from './tours';

const POPOVER_WIDTH = 340;
const POPOVER_GAP = 12;

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Resolve the bounding rect of the element flagged with the given
 *  `data-tour-target`. Returns null if the element is missing — in which
 *  case the engine falls back to a centered placement so the tour never
 *  hard-fails on a renamed marker. */
function findAnchor(target: string | null): AnchorRect | null {
  if (!target || typeof document === 'undefined') return null;
  const el = document.querySelector<HTMLElement>(`[data-tour-target="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

interface PopoverPosition {
  top: number;
  left: number;
  arrow: TourPlacement;
}

/** Compute popover position from anchor rect + desired placement. Tries
 *  the requested side first; flips to the opposite side if the popover
 *  would be clipped by the viewport. Falls back to centered if no anchor. */
function computePosition(
  anchor: AnchorRect | null,
  placement: TourPlacement,
  popoverHeight: number,
): PopoverPosition {
  if (!anchor || placement === 'center' || typeof window === 'undefined') {
    return {
      top: window.innerHeight / 2 - popoverHeight / 2,
      left: window.innerWidth / 2 - POPOVER_WIDTH / 2,
      arrow: 'center',
    };
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tries: TourPlacement[] = (() => {
    switch (placement) {
      case 'top':
        return ['top', 'bottom', 'right', 'left'];
      case 'bottom':
        return ['bottom', 'top', 'right', 'left'];
      case 'left':
        return ['left', 'right', 'bottom', 'top'];
      case 'right':
        return ['right', 'left', 'bottom', 'top'];
      default:
        return [placement];
    }
  })();

  for (const side of tries) {
    let top = 0;
    let left = 0;
    if (side === 'top') {
      top = anchor.top - popoverHeight - POPOVER_GAP;
      left = anchor.left + anchor.width / 2 - POPOVER_WIDTH / 2;
    } else if (side === 'bottom') {
      top = anchor.top + anchor.height + POPOVER_GAP;
      left = anchor.left + anchor.width / 2 - POPOVER_WIDTH / 2;
    } else if (side === 'left') {
      top = anchor.top + anchor.height / 2 - popoverHeight / 2;
      left = anchor.left - POPOVER_WIDTH - POPOVER_GAP;
    } else if (side === 'right') {
      top = anchor.top + anchor.height / 2 - popoverHeight / 2;
      left = anchor.left + anchor.width + POPOVER_GAP;
    }
    // Clamp to viewport with an 8px margin
    const fits =
      top >= 8 && top + popoverHeight <= vh - 8 && left >= 8 && left + POPOVER_WIDTH <= vw - 8;
    if (fits) {
      return {
        top,
        left,
        arrow:
          side === 'top'
            ? 'bottom'
            : side === 'bottom'
              ? 'top'
              : side === 'left'
                ? 'right'
                : 'left',
      };
    }
  }

  // Nothing fit — clamp the requested placement and accept overlap.
  const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);
  return {
    top: clamp(anchor.top + anchor.height + POPOVER_GAP, 8, vh - popoverHeight - 8),
    left: clamp(anchor.left, 8, vw - POPOVER_WIDTH - 8),
    arrow: 'top',
  };
}

interface TourEngineProps {
  /** Optional override — used when a custom step is being previewed. By
   *  default the engine reads from the active tour in context. */
  step?: TourStep;
}

/** Renders a backdrop + popover for the active tour step. Lives in a
 *  React portal-equivalent (fixed-positioned, z-50) so it floats above
 *  whatever is below — including the AppShell. */
export function TourEngine({ step: stepOverride }: TourEngineProps = {}) {
  const { activeTour, stepIndex, next, prev, end } = useTour();
  const navigate = useNavigate();
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<PopoverPosition>({
    top: 200,
    left: 200,
    arrow: 'center',
  });
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  // Under noUncheckedIndexedAccess, steps[stepIndex] is TourStep | undefined; coalesce
  // missing index back to null to match the local guarantee that this is TourStep | null.
  const step: TourStep | null =
    stepOverride ?? (activeTour ? (activeTour.steps[stepIndex] ?? null) : null);
  const isLast = activeTour ? stepIndex >= activeTour.steps.length - 1 : false;

  // If the current step requires navigating, do it before measuring. Use
  // an effect so the navigation happens during commit, then the next
  // render measures the new DOM.
  useEffect(() => {
    if (!step?.path) return;
    if (typeof window === 'undefined') return;
    if (window.location.pathname !== step.path) {
      navigate(step.path);
    }
  }, [step?.path, navigate]);

  // Position the popover. Re-measures on step change, viewport resize,
  // and scroll. Uses a layout effect so the user never sees a frame at
  // the stale position.
  useLayoutEffect(() => {
    if (!step) return;
    let raf = 0;
    const measure = () => {
      const a = findAnchor(step.target);
      setAnchor(a);
      const popH = popoverRef.current?.offsetHeight ?? 200;
      setPosition(computePosition(a, step.placement ?? 'bottom', popH));
    };
    // Two RAFs so we measure after the page has had a chance to lay out
    // the target element (lazy-loaded routes mount one frame after the
    // navigate() call).
    raf = requestAnimationFrame(() => requestAnimationFrame(measure));

    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [step]);

  // Keyboard nav: ArrowRight = next, ArrowLeft = prev, Esc = dismiss.
  useEffect(() => {
    if (!step) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        end(false);
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, next, prev, end]);

  if (!activeTour || !step) return null;

  const isCenter = position.arrow === 'center';

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-label={activeTour.name}
      aria-modal="true"
    >
      {/* Dim backdrop. The cutout around the anchor is implemented via a
          separate ring rather than an SVG mask — simpler, and keeps the
          target interactable if a step explicitly wants that. Click
          outside dismisses. */}
      <div
        className="absolute inset-0 bg-black/65 backdrop-blur-[1px]"
        onClick={() => end(false)}
      />

      {/* Anchor highlight — a glowing ring around the target. */}
      {anchor && !isCenter && (
        <div
          className="pointer-events-none absolute rounded-fos-md shadow-fos-lg ring-2 ring-fos-accent"
          style={{
            top: anchor.top - 4,
            left: anchor.left - 4,
            width: anchor.width + 8,
            height: anchor.height + 8,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.0)',
          }}
        />
      )}

      {/* Popover */}
      <div
        ref={popoverRef}
        onClick={(e) => e.stopPropagation()}
        style={{ top: position.top, left: position.left, width: POPOVER_WIDTH }}
        className={cn(
          'absolute animate-fos-fade-in',
          'rounded-fos-md border border-fos-border bg-fos-bg-card shadow-fos-lg',
          'p-4 flex flex-col gap-3',
        )}
      >
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-fos bg-fos-accent-bg text-fos-accent">
            <Sparkles size={12} />
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-fos-text-muted">
            {activeTour.name} · Step {stepIndex + 1} of {activeTour.steps.length}
          </span>
          <button
            type="button"
            onClick={() => end(false)}
            aria-label="Skip tour"
            className="ml-auto text-fos-text-muted transition-colors duration-fos hover:text-fos-text-primary"
          >
            <X size={14} />
          </button>
        </div>

        <h2 className="text-[15px] font-semibold leading-tight text-fos-text-primary">
          {step.title}
        </h2>
        <p className="text-[12px] leading-relaxed text-fos-text-secondary">{step.body}</p>

        {/* Step dots */}
        <div className="flex gap-1">
          {activeTour.steps.map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-1 flex-1 rounded-full transition-colors duration-fos',
                i <= stepIndex ? 'bg-fos-accent' : 'bg-fos-border',
              )}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          <FosButton
            size="xs"
            variant="ghost"
            onClick={() => end(true)}
            title="Skip and don't show again"
          >
            Skip
          </FosButton>
          <div className="flex items-center gap-1.5">
            <FosButton
              size="xs"
              variant="default"
              onClick={prev}
              disabled={stepIndex === 0}
              title="Previous step (←)"
            >
              <ArrowLeft size={11} /> Back
            </FosButton>
            <FosButton size="xs" variant="primary" onClick={next} title="Next (→)">
              {isLast ? 'Done' : 'Next'} <ArrowRight size={11} />
            </FosButton>
          </div>
        </div>

        <div className="font-mono text-[10px] text-fos-text-muted">
          ← / → navigate · esc to dismiss · reopen from ⌘K
        </div>
      </div>
    </div>
  );
}
